import { IJobAssemblySession, JobPartCategory } from "../models/Job";
import { IPartAssembly } from "../models/Part";
import { isJobPartActioned } from "./partCompletion";

/** The PauseReason the API writes when a timer is finished rather than paused. */
const FINISHED_REASON = "Finished";

export interface IAssemblyProgressColumn {
  id: string;
  name: string;
  completedCount: number;
  totalCount: number;
  /** A timer segment is open on this assembly right now. */
  timerRunning: boolean;
  /** The timer is stopped and its last stop was a Finish rather than a Pause. */
  timerFinished: boolean;
  /** Every part actioned, or - with no parts on the job - the timer finished. */
  isCompleted: boolean;
}

// Structural rather than Pick<IPartAssembly, ...> so a test can build one from just the ids.
type ProgressAssembly = Pick<IPartAssembly, "id" | "name"> & {
  partAssemblyPartCategories?: { partCategoryId?: string | null }[];
};
type ProgressSession = Pick<
  IJobAssemblySession,
  "partAssemblyId" | "dismantlingStart" | "dismantlingEnd" | "pauseReason"
>;

const timerState = (sessions: ProgressSession[]): Pick<IAssemblyProgressColumn, "timerRunning" | "timerFinished"> => {
  const timerRunning = sessions.some((s) => !s.dismantlingEnd);
  const lastClosed = [...sessions]
    .filter((s) => !!s.dismantlingEnd)
    .sort((a, b) => (b.dismantlingEnd ?? "").localeCompare(a.dismantlingEnd ?? ""))[0];

  return {
    timerRunning,
    timerFinished: !timerRunning && lastClosed?.pauseReason === FINISHED_REASON,
  };
};

/**
 * The job's strip-down as the overview's Assembly Progress shows it: one column per assembly
 * the job touches, in the order the assemblies are given.
 *
 * An assembly is included when the job has parts in it. With includeTimedWithoutParts (the TME
 * flow) it is also included when it has been timed on this job - an assembly with no parts is
 * still a station the operator stands at, and once its timer has been finished it is done, the
 * same rule the Production Dashboard applies. With parts, the parts decide: complete means every
 * part actioned (removed, damaged, missing or not accessible), and the timer alone can't finish
 * outstanding parts.
 */
export function buildAssemblyProgress(
  assemblies: ProgressAssembly[],
  jobPartCategories: JobPartCategory[],
  sessions: ProgressSession[],
  includeTimedWithoutParts: boolean
): IAssemblyProgressColumn[] {
  const columns: IAssemblyProgressColumn[] = [];

  assemblies.forEach((assembly) => {
    const categoryIds = new Set(
      (assembly.partAssemblyPartCategories ?? [])
        .map((category) => category.partCategoryId)
        .filter((categoryId): categoryId is string => !!categoryId)
    );
    const parts = jobPartCategories.filter(
      (jobPartCategory) =>
        !!jobPartCategory.partCategoryId && categoryIds.has(jobPartCategory.partCategoryId)
    );
    const own = sessions.filter((s) => s.partAssemblyId === assembly.id && !!s.dismantlingStart);

    if (parts.length === 0 && (!includeTimedWithoutParts || own.length === 0)) return;

    const completedCount = parts.filter(isJobPartActioned).length;
    const timer = timerState(own);

    columns.push({
      id: assembly.id,
      name: assembly.name,
      completedCount,
      totalCount: parts.length,
      ...timer,
      isCompleted: parts.length > 0 ? completedCount === parts.length : timer.timerFinished,
    });
  });

  return columns;
}
